<template>
  <div
    class="
      rentpress-shortcode-single-floorplan-information-wrapper
      rentpress-inherited-font-family
    "
    :style="{ '--primary-color': primaryColor }"
  >
    <v-banner
      v-if="floorplan.floorplan_specials_message"
      elevation="10"
      :color="primaryColor"
      :style="{ color: getContrastingTextColor(primaryColor) }"
      class="rentpress-inherited-font-family text-h6 text-center px-8"
      style="z-index: 1"
    >
      <div
        @click="
          navigateToExternalSite(floorplan.floorplan_specials_link);
          gtag('event', 'special_view_rentpress', {
            property_name: floorplan.property_name,
            city: floorplan.property_city,
            floorplan_name: floorplan.floorplan_post_title
          });
        "
        style="height:100%;width:100%;"
      >
        <span class="font-weight-black">
          <v-icon :style="{ color: getContrastingTextColor(primaryColor) }">{{
            $vuetify.icons.values.star
          }}</v-icon>
          SPECIAL:
        </span>
        {{ floorplan.floorplan_specials_message }}
      </div>
    </v-banner>
    <v-row justify="center" align="center" class="pa-2">
      <v-col xl="6" lg="6" md="5" sm="12" cols="auto" class="carousel-column">
        <v-carousel
          :style="{ maxHeight: '80vh', width: '100%' }"
          :height="floorplanInfoHeight"
          :show-arrows="carouselImages.length != 1"
          :hide-delimiters="carouselImages.length == 1"
          class="rentpress-floorplan-carousel"
        >
          <v-carousel-item
            v-for="(carouselImage, imageIndex) in carouselImages"
            :key="imageIndex"
            class="rentpress-floorplan-carousel-item"
          >
            <v-img
              ref="carouselImgs"
              :src="carouselImage.src"
              :alt="
                carouselImage.alt ||
                  floorplan.floorplan_post_title + ' image ' + imageIndex
              "
              contain
              @load="onCarouselImageLoad($event, imageIndex)"
              @click="viewImageFullscreen(imageIndex)"
              class="rentpress-floorplan-carousel-image"
            >
              <template v-slot:placeholder>
                <div class="d-flex align-center justify-center fill-height">
                  <v-progress-circular
                    :color="primaryColor"
                    indeterminate
                  ></v-progress-circular>
                </div>
              </template>
            </v-img>
          </v-carousel-item>
        </v-carousel>
        <div
          v-if="floorplan.floorplan_matterport_url || engrainMapLink"
          class="rentpress-floorplan-iframe-buttons rentpress-floorplan-under-image-buttons"
        >
          <a
            href="#rentpress-virtual-tour"
            class="floorplan-virtual-tour-button circle-button"
            :style="{ borderColor: primaryColor }"
            v-if="floorplan.floorplan_matterport_url"
          >
            <v-icon medium :color="primaryColor">{{
              $vuetify.icons.values.playCircleIcon
            }}</v-icon>
            <p :style="{ color: primaryColor }">Virtual Tour</p>
          </a>
          <a
            href="#single_floorplan_sightmap"
            class="floorplan-show-map-button circle-button"
            :style="{ borderColor: primaryColor }"
            v-if="hasUnits && engrainMapLink"
          >
            <v-icon medium :color="primaryColor">{{
              $vuetify.icons.values.mapMarker
            }}</v-icon>
            <p :style="{ color: primaryColor }">Show Map</p>
          </a>
        </div>
      </v-col>
      <v-overlay
        z-index="1000000"
        :value="galleryOverlay"
        opacity="0.85"
        v-touch="{
          left: () => gallerySwipe('Left'),
          right: () => gallerySwipe('Right'),
          up: () => gallerySwipe('Up'),
          down: () => gallerySwipe('Down')
        }"
      >
        <v-carousel
          :show-arrows="carouselImages.length != 1"
          :hide-delimiters="carouselImages.length == 1"
          v-model="selectedGalleryImage"
          height="100vh"
        >
          <v-carousel-item
            v-for="(galleryImage, galleryImageKey) in carouselImages"
            :key="galleryImageKey"
          >
            <v-sheet
              color="transparent"
              height="100vh"
              width="100vw"
              tile
              @click.self="galleryOverlay = false"
            >
              <v-row
                class="fill-height"
                align="center"
                justify="center"
                @click.self="galleryOverlay = false"
              >
                <v-lazy
                  :min-height="200"
                  :options="{ threshold: 0.5 }"
                  transition="fade-transition"
                >
                  <div class="rentpress-lightbox-image-wrapper">
                    <v-img
                      max-height="95vh"
                      :height="galleryImage.height"
                      max-width="95vw"
                      class="rentpress-floorplan-carousel-image-lightbox"
                      :src="galleryImage.src"
                      contain
                    />
                  </div>
                  <template v-slot:placeholder>
                    <div class="d-flex align-center justify-center fill-height">
                      <v-progress-circular
                        :color="
                          parentPropertyColor ? parentPropertyColor : 'primary'
                        "
                        indeterminate
                      ></v-progress-circular>
                    </div>
                  </template>
                </v-lazy>
              </v-row>
            </v-sheet>
          </v-carousel-item>
        </v-carousel>
        <v-btn
          @click="galleryOverlay = false"
          fab
          top
          right
          tonal
          large
          absolute
          z-index="999994"
          class="mt-12 rentpress-gallery-close-button"
          :color="primaryColor"
          style="filter: drop-shadow(1px 1px 10px #dedede4d);"
          :style="{
            color: getContrastingTextColor(primaryColor)
          }"
        >
          <v-icon x-large>{{ $vuetify.icons.values.close }}</v-icon>
        </v-btn>
      </v-overlay>
      <v-col xl="6" lg="6" md="7" sm="12" cols="auto">
        <v-card tile flat class="pl-2 pr-3" ref="floorplanInformation">
          <div class="rentpress-floorplan-information-top-wrapper">
            <div class="rentpress-floorplan-information-title-price-wrapper">
              <v-card-title
                v-if="useFPName"
                class="rentpress-inherited-font-family text-h3 secondary--text pl-0 rentpress-single-floorplan-title"
              >
                {{ floorplan.floorplan_post_title }}
              </v-card-title>
              <v-card-text
                class="rentpress-inherited-font-family font-weight-bold text-h5 pl-0"
              >
                <span
                  v-if="
                    !disablePrice &&
                      floorplan.floorplan_rent_type_selection ==
                        'Minimum - Maximum' &&
                      floorplan.floorplan_rent_min &&
                      floorplan.floorplan_rent_max &&
                      floorplan.floorplan_rent_min !=
                        floorplan.floorplan_rent_max
                  "
                >
                  {{ formatter.format(floorplan.floorplan_rent_min) }} -
                  {{ formatter.format(floorplan.floorplan_rent_max) }}
                </span>
                <span
                  v-else-if="
                    !disablePrice &&
                      floorplan.floorplan_rent_type_selection_cost &&
                      floorplan.floorplan_rent_type_selection_cost > 100
                  "
                >
                  Starting at
                  {{
                    this.formatter.format(
                      floorplan.floorplan_rent_type_selection_cost
                    )
                  }}
                </span>
              </v-card-text>
            </div>
            <div
              v-if="floorplan.floorplan_matterport_url || engrainMapLink"
              class="rentpress-floorplan-iframe-buttons rentpress-floorplan-title-buttons"
            >
              <a
                href="#rentpress-virtual-tour"
                class="floorplan-virtual-tour-button circle-button"
                :style="{ borderColor: primaryColor }"
                v-if="floorplan.floorplan_matterport_url"
              >
                <v-icon medium :color="primaryColor">{{
                  $vuetify.icons.values.playCircleIcon
                }}</v-icon>
                <p :style="{ color: primaryColor }">Virtual Tour</p>
              </a>
              <a
                href="#single_floorplan_sightmap"
                class="floorplan-show-map-button circle-button"
                :style="{ borderColor: primaryColor }"
                v-if="hasUnits && engrainMapLink"
              >
                <v-icon medium :color="primaryColor">{{
                  $vuetify.icons.values.mapMarker
                }}</v-icon>
                <p :style="{ color: primaryColor }">Show Map</p>
              </a>
            </div>
          </div>

          <v-card-title
            v-if="!useFPName"
            class="rentpress-inherited-font-family text-h3 secondary--text pl-0"
          >
            {{ displayBedroomsText }}
          </v-card-title>
          <v-card-subtitle
            v-if="useFPName"
            class="rentpress-inherited-font-family text-h5 pb-1 pt-2 pl-0 rentpress-single-floorplan-main-info-block"
          >
            <span
              v-if="displayBedroomsText"
              class="rentpress-floorplan-bedroom-text"
            >
              {{ displayBedroomsText }}
            </span>
            <span v-if="sqFtText" class="rentpress-floorplan-sqft">
              <span
                v-if="displayBedroomsText && sqFtText"
                class="rentpress-floorplan-information-dot-sep"
              >
                •
              </span>
              {{ sqFtText }}
            </span>
            <span
              v-if="
                (!this.options
                  .rentpress_disable_pricing_section_disable_pricing ||
                  !this.floorplan.property_rent_type_selection ===
                    'Disabled') &&
                  displayDepositText
              "
              class="rentpress-floorplan-deposit-range"
            >
              <span
                v-if="(displayBedroomsText && sqFtText) || displayDepositText"
                class="rentpress-floorplan-information-dot-sep"
              >
                •
              </span>
              {{ displayDepositText }} Deposit
            </span>
          </v-card-subtitle>
          <div v-if="floorplan.units.length > 0">
            <div v-if="floorplan.units.length > 1" key="choose-apartment-text">
              <v-card-text
                class="
                rentpress-inherited-font-family
                rentpress-floorplan-single-information-subtitle
                text-h6
                font-weight-bold
                pl-0
              "
              >
                Choose An Apartment
              </v-card-text>
            </div>
            <div
              v-if="floorplan.units.length > 2"
              key="units-sort-dropdown"
              class="rentpress-units-sort-dropdown"
              style="margin-bottom: 16px;"
            >
              <v-select
                v-model="selectedSort"
                :items="sortOptions"
                label="Sort by..."
                outlined
                hide-details
                style="max-width: 200px;"
              >
                <template v-slot:selection="data">
                  <span class="unit-sort-selection">{{ data.item.text }}</span>
                </template>
              </v-select>
            </div>
            <div
              class="rentpress-units-selection-wrapper rentpress-units-wrapper min-width-100"
            >
              <div v-if="floorplan.units.length > 2" class="d-none d-md-block">
                <v-icon
                  x-large
                  :color="primaryColor"
                  @click="scrollUnitSlider('left')"
                  >{{ $vuetify.icons.values.prev }}</v-icon
                >
              </div>
              <div class="rentpress-units-selection-container" ref="unitSlider">
                <div
                  v-for="(unit, index) in sortedUnits"
                  :key="unit.unit_code"
                  class="rentpress-unit-slider-item"
                  :ref="el => (unitRefs[index] = el)"
                  v-on="
                    floorplan.units.length > 2
                      ? { wheel: horizontalScroll }
                      : {}
                  "
                >
                  <div @click="selectThisUnit(unit)">
                    <unit-card
                      :unit="unit"
                      :options="options"
                      :formatter="formatter"
                      :primaryColor="primaryColor"
                      :disabledPrice="
                        floorplan.floorplan_rent_type_selection == 'Disabled'
                      "
                      :isActive="isActiveUnit(unit.unit_code)"
                    />
                  </div>
                </div>
              </div>
              <div v-if="floorplan.units.length > 2" class="d-none d-md-block">
                <v-icon
                  x-large
                  :color="primaryColor"
                  @click="scrollUnitSlider('right')"
                  >{{ $vuetify.icons.values.next }}</v-icon
                >
              </div>
            </div>
          </div>
          <div
            v-else
            class="rentpress-inherited-font-family rentpress-units-wrapper rentpress-no-units-text"
          >
            <h4 class="rentpress-no-units-headline">No Apartments Available</h4>
            <p class="rentpress-no-units-message">
              This is one of our most popular layouts. No apartments are
              currently available.
            </p>
          </div>
          <div
            v-if="selectedUnit.unit_specials_message"
            class="rentpress-units-special-container"
          >
            <div
              class="rentpress-inherited-font-family text-h6 font-weight-bold"
            >
              Current Special for
              {{ selectedUnit.unit_name }}:
            </div>
            <div class="rentpress-unit-special-text">
              <p v-html="parsedUnitSpecial"></p>
            </div>
          </div>
          <div
            v-if="selectedUnitFeatures.length"
            class="rentpress-units-feature-container"
          >
            <v-card-text
              class="rentpress-inherited-font-family text-h6 font-weight-bold"
              >Features for {{ selectedUnit.unit_name }}:
            </v-card-text>
            <div id="rentpress-unit-features-list">
              <div
                v-for="feature in selectedUnitFeatures"
                :key="feature.Rank"
                class="rentpress-unit-features-list-item"
              >
                <v-icon :color="primaryColor">{{
                  $vuetify.icons.values.next
                }}</v-icon>
                {{ feature.Title }}
              </div>
            </div>
          </div>

          <div
            v-if="
              !disablePrice &&
                selectedUnitTerms.length &&
                propertyHasLeaseTermsSelected
            "
            class="rentpress-units-term-container"
          >
            <v-card-text
              class="rentpress-inherited-font-family text-h6 font-weight-bold"
              >Available Lease Terms for {{ selectedUnit.unit_name }}
            </v-card-text>
            <v-row>
              <v-col xl="5" lg="6" md="5" sm="7" cols="12">
                <v-menu
                  v-model="termRentDisplay"
                  :close-on-content-click="true"
                  bottom
                  origin="center center"
                  transition="scale-transition"
                >
                  <template v-slot:activator="{ on }">
                    <div v-on="on" class="rentpress-inherited-font-family">
                      <v-select
                        solo
                        :label="
                          selectedLeaseTerm
                            ? selectedLeaseTerm +
                              ' Months: ' +
                              formatter.format(selectedLeaseRent)
                            : 'Show Terms'
                        "
                        readonly
                      ></v-select>
                    </div>
                  </template>
                  <v-list
                    id="rentpress-units-term-menu-list"
                    class="rentpress-inherited-font-family"
                    style="overflow-y: auto; max-height: 60vh"
                  >
                    <v-list-item>
                      <v-row justify="end" class="font-weight-bold">
                        <v-col cols="5"> Lease Term </v-col>
                        <v-col cols="7"> Price Per Month </v-col>
                      </v-row>
                    </v-list-item>
                    <v-list-item
                      v-for="(term, termIndex) in selectedUnitTerms"
                      v-bind:key="termIndex"
                      :value="term.Rent"
                      :class="
                        isBestLeaseValue(term)
                          ? 'font-weight-bold term-list-item term-list-item-best'
                          : 'term-list-item'
                      "
                    >
                      <v-row
                        @click="setDesiredLeaseTerm(term)"
                        style="cursor: pointer"
                      >
                        <v-col cols="5"> {{ term.Term }} Months </v-col>
                        <v-col cols="7">
                          {{ formatter.format(term.Rent) }}
                          <span
                            v-if="isBestLeaseValue(term)"
                            style="white-space: nowrap"
                          >
                            (Best Value)</span
                          >
                        </v-col>
                      </v-row>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>
            </v-row>
          </div>
          <v-row
            justify="start"
            class="pt-3 rentpress-single-floorplan-main-info-block-cta-wrapper"
          >
            <v-col
              v-if="options.rentpress_application_link_section_url_tour != ''"
              cols="12"
              xl="4"
              lg="4"
              md="4"
              sm="4"
            >
              <v-btn
                :color="primaryColor"
                x-large
                block
                :href="tourLink"
                @click="
                  gtag('event', 'scheduletour_click_rentpress', {
                    property_name: floorplan.property_name,
                    city: floorplan.property_city,
                    floorplan_name: floorplan.floorplan_post_title
                  })
                "
                class="rentpress-application-link-section-url-tour-button rentpress-cta-tour"
                :style="{ color: getContrastingTextColor(primaryColor) }"
              >
                Schedule Tour
              </v-btn>
            </v-col>
            <v-col cols="12" xl="4" lg="4" md="4" sm="4">
              <div
                v-if="floorplan.floorplan_parent_property_contact_type == '3'"
              >
                <v-btn
                  block
                  :color="primaryColor"
                  class="rentpress-inherited-font-family rentpress-cta-info"
                  :style="
                    getOutlinedButtonStyle(
                      primaryColor,
                      options.rentpress_application_link_section_url_tour != ''
                    )
                  "
                  x-large
                  :outlined="
                    options.rentpress_application_link_section_url_tour != ''
                  "
                  @click="
                    gtag('event', 'requestinfo_click_rentpress', {
                      property_name: floorplan.property_name,
                      city: floorplan.property_city,
                      floorplan_name: floorplan.floorplan_post_title
                    });
                    openGravityFormsModal(floorplan.floorplan_post_id);
                  "
                >
                  Request Info
                </v-btn>
                <v-dialog
                  v-model="propertyGravityForm"
                  fullscreen
                  hide-overlay
                  transition="dialog-bottom-transition"
                  scrollable
                >
                  <v-card tile>
                    <v-btn
                      color="#303030"
                      fab
                      dark
                      small
                      absolute
                      top
                      right
                      @click="propertyGravityForm = ''"
                      class="mt-6 mr-n3"
                      elevation="10"
                    >
                      <v-icon>{{ $vuetify.icons.values.close }}</v-icon>
                    </v-btn>
                    <div
                      class="floorplan-form-pop-up"
                      v-html="floorplanForm"
                      ref="gravityform"
                    ></div>
                  </v-card>
                </v-dialog>
              </div>
              <div v-else>
                <v-btn
                  block
                  :color="primaryColor"
                  class="rentpress-inherited-font-family rentpress-cta-info"
                  :style="
                    getOutlinedButtonStyle(
                      primaryColor,
                      options.rentpress_application_link_section_url_tour != ''
                    )
                  "
                  x-large
                  :outlined="
                    options.rentpress_application_link_section_url_tour != ''
                  "
                  :href="contactLink"
                  @click="
                    gtag('event', 'requestinfo_click_rentpress', {
                      property_name: floorplan.property_name,
                      city: floorplan.property_city,
                      floorplan_name: floorplan.floorplan_post_title
                    })
                  "
                >
                  Request Info
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" xl="4" lg="4" md="4" sm="4">
              <v-btn
                v-if="hasUnits && applyLink"
                class="rentpress-inherited-font-family rentpress-cta-apply"
                :color="primaryColor"
                block
                x-large
                outlined
                :href="applyLink"
                :target="
                  options.rentpress_apply_link_section === 'New Window'
                    ? '_blank'
                    : '_self'
                "
                :style="getOutlinedButtonStyle(primaryColor, true)"
                @click="
                  gtag('event', 'applynow_click_rentpress', {
                    property_name: floorplan.property_name,
                    city: floorplan.property_city,
                    floorplan_name: floorplan.floorplan_post_title
                  })
                "
              >
                Apply Now
              </v-btn>
              <v-btn
                v-if="
                  !hasUnits &&
                    options.rentpress_application_link_section_url_waitlist !=
                      ''
                "
                :color="primaryColor"
                class="rentpress-inherited-font-family rentpress-cta-waitlist"
                :style="getOutlinedButtonStyle(primaryColor, true)"
                x-large
                block
                outlined
                :href="waitlistLink"
                @click="
                  gtag('event', 'joinwaitlist_click_rentpress', {
                    property_name: floorplan.property_name,
                    city: floorplan.property_city,
                    floorplan_name: floorplan.floorplan_post_title
                  })
                "
              >
                Join Waitlist
              </v-btn>
            </v-col>
          </v-row>
          <v-row
            class="rentpress-single-floorplan-main-info-block-social-wrapper"
          >
            <v-col cols="auto">
              <p
                class="
                  rentpress-inherited-font-family
                  font-weight-bold
                  secondary--text
                  pt-1
                  rentpress-share-icons
                "
              >
                SHARE
              </p>
            </v-col>
            <v-col cols="auto">
              <v-tooltip bottom open-delay="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    :href="emailLink"
                    target="_blank"
                    class="rentpress-share-icon-email"
                    @mouseover="shareHover = 'email'"
                    @mouseleave="shareHover = ''"
                  >
                    <v-icon
                      :color="shareHover === 'email' ? primaryColor : ''"
                      class="rentpress-share-icon-email-icon"
                      >{{ $vuetify.icons.values.email }}</v-icon
                    >
                  </v-btn>
                </template>
                Share via email
              </v-tooltip>
              <v-tooltip bottom open-delay="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    :href="smsLink"
                    class="rentpress-share-icon-sms hidden-md-and-up"
                    @mouseover="shareHover = 'sms'"
                    @mouseleave="shareHover = ''"
                  >
                    <v-icon
                      :color="shareHover === 'sms' ? primaryColor : ''"
                      class="rentpress-share-icon-sms-icon"
                      >{{ $vuetify.icons.values.messageBadge }}</v-icon
                    >
                  </v-btn>
                </template>
                Share via SMS
              </v-tooltip>
              <v-tooltip bottom open-delay="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    href="javascript:void(0)"
                    @click.prevent="copyToClipboard"
                    class="rentpress-share-icon-copy"
                    @mouseover="shareHover = 'copy'"
                    @mouseleave="shareHover = ''"
                  >
                    <v-icon
                      :color="shareHover === 'copy' ? primaryColor : ''"
                      class="rentpress-share-icon-copy-icon"
                      >{{ $vuetify.icons.values.linkVariantPlus }}</v-icon
                    >
                  </v-btn>
                </template>
                Copy link
              </v-tooltip>
              <v-tooltip bottom open-delay="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    :href="facebookLink"
                    target="_blank"
                    class="rentpress-share-icon-facebook"
                    @mouseover="shareHover = 'facebook'"
                    @mouseleave="shareHover = ''"
                  >
                    <v-icon
                      :color="shareHover === 'facebook' ? primaryColor : ''"
                      class="rentpress-share-icon-facebook-icon"
                      >{{ $vuetify.icons.values.facebook }}</v-icon
                    >
                  </v-btn>
                </template>
                Share via Facebook
              </v-tooltip>
            </v-col>
            <v-spacer></v-spacer>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <script v-html="jsonld" type="application/ld+json"></script>
  </div>
</template>

<script>
import unitCard from "./unit-card.vue";

export default {
  name: "single-floorplan-information",
  data: function() {
    return {
      hasUnits: false,
      useFPName: true,
      hasSelectedUnit: false,
      propertyHasLeaseTermsSelected: false,
      selectedUnit: {},
      selectedUnitTerms: [],
      selectedUnitFeatures: [],
      selectedLeaseTerm: "",
      selectedLeaseRent: "",
      bestLeaseValue: "",
      image: this.options.rentpress_default_floor_plan_image_section,
      carouselImages: [],
      carouselImageHeights: [],
      sqFtText: "",
      facebookLink: "",
      emailLink: "",
      smsLink: "",
      propertyGravityForm: "",
      formInputs: "",
      termRentDisplay: false,
      displayBedroomsText: this.getTextForBedroomsDisplay(),
      selectedGalleryImage: 0,
      galleryOverlay: false,
      secondary: this.options.rentpress_secondary_accent_color_section_input,
      unitSliderScrollAmount: 0,
      disablePrice: this.options
        .rentpress_disable_pricing_section_disable_pricing,
      floorplanInfoHeight: "100%",
      floorplanInfoHeightChecks: 0,
      selectedSort: "available", // default sort
      sortOptions: [
        { text: "Available Date", value: "available" },
        { text: "Price", value: "price" },
        { text: "Size", value: "sqft" },
        { text: "Name", value: "name" }
      ],
      unitRefs: [],
      shareHover: "",
      jsonld: {
        // single floor plan schema
        "@type": "ItemPage",
        primaryImageOfPage: this.floorplan.floorplan_image,
        significantLink: this.floorplan.applyLink,
        about: {
          "@type": "Product",
          name: this.floorplan.floorplan_post_title,
          image: this.floorplan.floorplan_image,
          description: this.floorplan.floorplan_description,
          brand: this.options.site_name,
          offers: {
            "@type": "aggregateOffer",
            lowPrice: this.floorplan.floorplan_rent_min,
            highPrice: this.floorplan.floorplan_rent_max,
            priceCurrency: "USD",
            offerCount: this.floorplan.floorplan_units_available
          },
          sku: this.floorplan.floorplan_code
        },
        breadcrumb: [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: this.options.site_name + " Home",
                item: this.options.site_url
              },
              {
                "@type": "ListItem",
                position: 3,
                // name: this.property.property_name,
                item: this.floorplan.floorplan_parent_property_post_link
              }
            ]
          }
        ],
        mainEntity: [
          {
            "@type": "FloorPlan",
            image: this.floorplan.floorplan_image,
            description: this.floorplan.floorplan_description,
            name: this.floorplan.floorplan_post_title,
            url: this.floorplan.floorplan_post_link,
            floorsize: this.floorplan.floorplan_sqft_min,
            // isPlanForApartment: this.floorplan.floorplan_parent_property_name,
            numberOfBedrooms: this.floorplan.floorplan_bedrooms,
            numberOfBathroomsTotal: this.floorplan.floorplan_bathrooms,
            petsAllowed: this.displayPetText
          },
          {
            containedInPlace: {
              "@type": "LocalBusiness",
              // name: this.floorplan.floorplan_parent_property_name,
              // description: this.floorplan.property_description,
              priceRange: "",
              // telephone: this.floorplan.property_phone_number,
              image: this.floorplan.floorplan_image,
              address: {
                "@type": "PostalAddress"
                // streetAddress: this.floorplan.property_address,
                // addressLocality: this.floorplan.property_city,
                // addressRegion: this.floorplan.property_state,
                // postalCode: this.floorplan.property_zip
              },
              geo: {
                "@type": "GeoCoordinates"
                // latitude: this.floorplan.property_latitude,
                // longitude: this.floorplan.property_longitude
              },
              "@id": this.floorplan.floorplan_parent_property_post_link
            }
          }
        ],
        "@context": "https://schema.org"
      }
    };
  },
  props: {
    floorplan: Object,
    options: Object,
    formatter: Intl.NumberFormat,
    shortcode: {
      type: [String, Object],
      default: ""
    },
    sortBy: {
      type: String,
      default: "available"
    }
  },
  computed: {
    primaryColor() {
      const color =
        this.floorplan.property_use_accent_color == "1"
          ? this.floorplan.property_accent_color
          : this.options.rentpress_accent_color_section_input;
      return color;
    },
    engrainMapLink() {
      const link = this.floorplan.floorplan_parent_property_engrain_url;
      if (link && typeof link === "string") {
        return link;
      }
      return "";
    },
    floorplanForm() {
      let contact_type = this.floorplan.floorplan_parent_property_contact_type;
      const formEl = document.getElementById(
        this.floorplan.floorplan_parent_property_code + "_form_wrapper"
      );
      if (formEl && contact_type && contact_type != "Global Setting") {
        return formEl.innerHTML;
      }
      return "";
    },
    displayDepositText() {
      let depositMin = Number(this.floorplan.floorplan_deposit_min);
      let depositMax = Number(this.floorplan.floorplan_deposit_max);

      const globalSetting = this.options
        .rentpress_deposit_settings_section_deposit_settings;
      const propSetting = Number(
        this.floorplan.floorplan_parent_property_deposit_setting
      );
      const applyRule = rule => {
        switch (rule) {
          case "Always Show":
            return;
          case "Exclude $0 Deposits":
            if (depositMin === 0) depositMin = null;
            if (depositMax === 0) depositMax = null;
            return;
          case "Show None":
            depositMin = null;
            depositMax = null;
            return;
        }
      };
      if (propSetting === 1) {
        applyRule(globalSetting);
      } else if (propSetting === 2) {
        applyRule("Always Show");
      } else if (propSetting === 3) {
        applyRule("Exclude $0 Deposits");
      } else if (propSetting === 4) {
        applyRule("Show None");
      } else {
        // fallback to global
        applyRule(globalSetting);
      }
      // Build display text
      if (depositMin === null && depositMax === null) return "";
      if (depositMin === depositMax) {
        return `$${depositMin}`;
      }
      if (depositMin !== null && depositMax !== null) {
        return `$${depositMin} - $${depositMax}`;
      }
      if (depositMin !== null) {
        return `$${depositMin}`;
      }
      if (depositMax !== null) {
        return `$${depositMax}`;
      }
      return "";
    },
    sortedUnits() {
      return [...this.floorplan.units].sort((a, b) => {
        switch (this.selectedSort) {
          case "price":
            return (
              (a.unit_rent_type_selection_cost || 0) -
              (b.unit_rent_type_selection_cost || 0)
            );
          case "sqft":
            return (a.unit_sqft || 0) - (b.unit_sqft || 0);
          case "name":
            return (a.unit_name || "").localeCompare(b.unit_name || "");
          case "available":
          default:
            return (
              new Date(a.unit_available_on || 0) -
              new Date(b.unit_available_on || 0)
            );
        }
      });
    },
    unitParamString() {
      let paramString = "";
      if (this.hasSelectedUnit && this.selectedUnit.unit_code) {
        paramString =
          "&unit_name=" +
          this.selectedUnit.unit_name +
          "&unit_code=" +
          this.selectedUnit.unit_code;
        paramString = this.selectedLeaseTerm
          ? paramString + "&leaseTerm=" + this.selectedLeaseTerm
          : paramString;
      }
      return paramString;
    },
    parsedUnitSpecial() {
      const msg = this.selectedUnit.unit_specials_message;
      if (!msg) return "";
      try {
        const parsed = JSON.parse(msg);
        return Array.isArray(parsed) ? parsed[0] : parsed;
      } catch {
        return msg;
      }
    },
    applyLink() {
      if (this.hasSelectedUnit && this.selectedUnit.unit_availability_url) {
        return this.selectedUnit.unit_availability_url;
      } else if (this.floorplan.floorplan_availability_url) {
        return this.floorplan.floorplan_availability_url;
      } else {
        return this.options
          .rentpress_application_link_section_default_application_url;
      }
    },
    contactLink() {
      let contactURL = this.options
        .rentpress_application_link_section_url_contact
        ? this.options.rentpress_application_link_section_url_contact
        : window.location.origin + "/contact";
      const baseURL =
        this.floorplan.floorplan_parent_property_contact_link != "" &&
        this.floorplan.floorplan_parent_property_contact_link != null
          ? this.floorplan.floorplan_parent_property_contact_link
          : contactURL;
      let queryStringDelimiter = baseURL.includes("?") ? "&" : "?";
      return (
        baseURL +
        queryStringDelimiter +
        "property_code=" +
        this.floorplan.floorplan_parent_property_code +
        "&property_name=" +
        this.floorplan.property_name +
        "&floorplan_code=" +
        this.floorplan.floorplan_code +
        "&floorplan_name=" +
        this.floorplan.floorplan_name +
        "&floorplan_bed=" +
        this.floorplan.floorplan_bedrooms +
        this.unitParamString
      );
    },
    tourLink() {
      let queryStringDelimiter = this.options.rentpress_application_link_section_url_tour.includes(
        "?"
      )
        ? "&"
        : "?";
      return (
        this.options.rentpress_application_link_section_url_tour +
        queryStringDelimiter +
        "property_code=" +
        this.floorplan.floorplan_parent_property_code +
        "&property_name=" +
        this.floorplan.property_name +
        "&floorplan_code=" +
        this.floorplan.floorplan_code +
        "&floorplan_name=" +
        this.floorplan.floorplan_name +
        "&floorplan_bed=" +
        this.floorplan.floorplan_bedrooms +
        this.unitParamString
      );
    },
    waitlistLink() {
      let queryStringDelimiter = this.options.rentpress_application_link_section_url_waitlist.includes(
        "?"
      )
        ? "&"
        : "?";
      return (
        this.options.rentpress_application_link_section_url_waitlist +
        queryStringDelimiter +
        "property_code=" +
        this.floorplan.floorplan_parent_property_code +
        "&property_name=" +
        this.floorplan.property_name +
        "&floorplan_code=" +
        this.floorplan.floorplan_code +
        "&floorplan_name=" +
        this.floorplan.floorplan_name +
        "&floorplan_bed=" +
        this.floorplan.floorplan_bedrooms +
        this.unitParamString
      );
    }
  },
  methods: {
    openGravityFormsModal(id) {
      this.propertyGravityForm = id;
      this.$nextTick(() => {
        const form = this.$refs["gravityform"];
        if (form && form !== undefined && form !== null) {
          const formFields = form.querySelectorAll("input");
          for (let i = formFields.length - 1; i >= 0; i--) {
            let field = formFields[i];
            if (field.value === "floorplan_code") {
              field.value = this.floorplan.floorplan_code;
            }
          }
        }
      });
    },
    horizontalScroll(e) {
      e.preventDefault();
      this.$refs.unitSlider.scrollLeft += e.deltaY;
    },
    getContrastingTextColor(color) {
      if (!color) return "#202124";
      const c = color.replace("#", "");
      const rgb = Number.parseInt(c, 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? "#202124" : "#FDFDFD";
    },
    // Determines if a color is dark enough to contrast on white, courtesy WCAG
    isColorDarkEnough(color) {
      if (!color) return false;
      const c = color.replace("#", "");
      const rgb = Number.parseInt(c, 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      // If brightness is less than or equal to 128, it's dark enough
      return brightness <= 128;
    },
    getOutlinedButtonStyle(color, outlined) {
      // Only apply for outlined buttons
      if (!outlined) {
        return { color: this.getContrastingTextColor(color) };
      }
      if (this.isColorDarkEnough(color)) {
        return {
          color: color,
          borderColor: color
        };
      } else {
        return {
          color: "#000000",
          borderColor: "#000000"
        };
      }
    },
    scrollUnitSlider(direction) {
      if (direction === "right") {
        this.unitSliderScrollAmount = this.unitSliderScrollAmount + 260;
      }
      if (direction === "left") {
        this.unitSliderScrollAmount = this.unitSliderScrollAmount - 260;
      }
      this.$refs["unitSlider"].scrollTo({
        top: 0,
        left: this.unitSliderScrollAmount,
        behavior: "smooth"
      });
    },
    updateUrlParameter(param, value) {
      const url = new URL(window.location.href);
      if (value) {
        url.searchParams.set(param, value);
      } else {
        url.searchParams.delete(param);
      }
      window.history.replaceState({}, "", url.toString());
    },
    getTextForBedroomsDisplay() {
      let bedDisplayText = "";
      if (this.floorplan.floorplan_bedrooms) {
        if (this.floorplan.floorplan_bedrooms === "0") {
          bedDisplayText = "Studio";
        } else {
          bedDisplayText = this.floorplan.floorplan_bedrooms + " Bed";
        }
      }

      let bathsDisplayText = this.floorplan.floorplan_bathrooms
        ? this.floorplan.floorplan_bathrooms + " Bath"
        : "";

      let bedBathDivider = bedDisplayText && bathsDisplayText ? " | " : "";

      return bedDisplayText + bedBathDivider + bathsDisplayText;
    },
    navigateToExternalSite(newLocation) {
      // exit out of function if no link, this only happens with banner link, if in the future we switch from the whole banner being a link to a button, this can be removed
      if (!newLocation) {
        return;
      }
      if (newLocation.includes("http")) {
        window.open(newLocation, "_blank");
      } else {
        window.open("http://" + newLocation, "_blank");
      }
    },
    navigateTo(newLocation) {
      window.location = newLocation.includes("http")
        ? newLocation
        : "http://" + newLocation;
    },
    deselectUnit() {
      this.selectedUnit = {};
      this.hasSelectedUnit = false;
      this.selectedUnitFeatures = [];
      this.selectedUnitTerms = [];
      this.updateUrlParameter("unit_name", null);
      this.updateUrlParameter("unit_code", null);
    },
    parseUnitData(unit) {
      try {
        this.selectedUnitFeatures = JSON.parse(unit.unit_features) || [];
      } catch {
        this.selectedUnitFeatures = [];
      }
      try {
        this.selectedUnitTerms = JSON.parse(unit.unit_rent_terms) || [];
      } catch {
        this.selectedUnitTerms = [];
      }
    },
    setBestLeaseValue() {
      if (!this.selectedUnitTerms.length || !this.propertyHasLeaseTermsSelected)
        return;

      for (let i = this.selectedUnitTerms.length - 1; i >= 0; i--) {
        if (
          this.formatter.format(this.selectedUnitTerms[i].Rent) ===
          this.formatter.format(this.selectedUnit.unit_rent_type_selection_cost)
        ) {
          this.bestLeaseValue = this.selectedUnitTerms[i].Term;
          break;
        }
      }
    },
    selectThisUnit(selection) {
      if (this.selectedUnit.unit_code === selection.unit_code) {
        this.deselectUnit();
        return;
      }

      // Select unit
      this.selectedUnit = selection;
      this.unitSelected(selection);
      this.hasSelectedUnit = true;

      this.propertyHasLeaseTermsSelected =
        selection.unit_rent_type_selection === "Term Rent" &&
        selection.unit_rent_terms &&
        selection.unit_rent_terms.length > 0;

      this.selectedLeaseTerm = "";
      this.bestLeaseValue = "";

      this.parseUnitData(selection);
      this.updateUrlParameter("unit_name", this.selectedUnit.unit_name);
      this.setBestLeaseValue();

      this.setUpCarousel();

      // Scroll selected unit into view
      this.$nextTick(() => this.scrollUnitIntoView(selection));
    },
    scrollUnitIntoView(unit) {
      const index = this.sortedUnits.findIndex(
        u => u.unit_code === unit.unit_code
      );
      if (index !== -1 && this.unitRefs[index]) {
        const slider = this.$refs.unitSlider;
        const item = this.unitRefs[index];
        const scrollLeft =
          item.offsetLeft - slider.offsetWidth / 2 + item.offsetWidth / 2;
        slider.scrollTo({ left: scrollLeft, behavior: "smooth" });
      }
    },
    setDesiredLeaseTerm(term) {
      this.selectedLeaseTerm = term.Term;
      this.selectedLeaseRent = term.Rent;
    },
    isActiveUnit(unit_code) {
      return unit_code == this.selectedUnit.unit_code;
    },
    isBestLeaseValue(term) {
      return term.Term == this.bestLeaseValue;
    },
    isNonEmptyArray(value) {
      return Array.isArray(value) && value.length > 0;
    },
    pushCarouselImage(src, alt) {
      this.carouselImages.push({ src, alt });
    },
    processUnitImagesArray(imagesArray, defaultAltPrefix) {
      if (!this.isNonEmptyArray(imagesArray)) return;
      imagesArray.forEach(image => {
        let imgObj;
        if (typeof image === "string") {
          imgObj = {
            src: image,
            alt: `${defaultAltPrefix} image of ${this.selectedUnit.unit_name} at ${this.floorplan.floorplan_post_title} floor plan`
          };
          this.pushCarouselImage(imgObj.src, imgObj.alt);
        } else if (image?.url) {
          imgObj = {
            src: image.url,
            alt:
              image.alt_text ||
              `Image for ${this.floorplan.floorplan_post_title} floor plan`
          };
          this.pushCarouselImage(imgObj.src, imgObj.alt);
        }
      });
    },
    setUpCarousel() {
      this.carouselImages = [];

      // Selected unit images
      if (this.selectedUnit.unit_images) {
        const imagesArray = JSON.parse(this.selectedUnit.unit_images);
        this.processUnitImagesArray(imagesArray, "Image");
      }

      // Featured and floorplan images
      if (this.floorplan.floorplan_featured_image) {
        this.pushCarouselImage(
          this.floorplan.floorplan_featured_image,
          `Image of ${this.floorplan.floorplan_post_title} floor plan`
        );
      }
      if (this.floorplan.floorplan_image) {
        this.pushCarouselImage(
          this.floorplan.floorplan_image,
          `Image of ${this.floorplan.floorplan_post_title} floor plan`
        );
      }

      // Floorplan images array
      if (this.floorplan.floorplan_images) {
        const imagesArray = JSON.parse(this.floorplan.floorplan_images);
        this.processUnitImagesArray(imagesArray, "Floorplan");
      }

      // Fallbacks
      if (!this.isNonEmptyArray(this.carouselImages)) {
        if (this.floorplan.floorplan_unit_images) {
          const unit_images = JSON.parse(this.floorplan.floorplan_unit_images);
          if (unit_images?.[0]) {
            this.pushCarouselImage(
              unit_images[0],
              `Unit image of ${this.floorplan.floorplan_post_title}`
            );
          }
        } else {
          this.pushCarouselImage(
            this.options.rentpress_default_floor_plan_image_section,
            `Placeholder image of ${this.floorplan.floorplan_post_title}`
          );
        }
      }
    },
    gallerySwipe(direction) {
      this.swipeDirection = direction;
      if (direction === "Right") {
        this.selectedGalleryImage = this.selectedGalleryImage++;
      }
      if (direction === "Left") {
        this.selectedGalleryImage = this.selectedGalleryImage--;
      }
      if (direction === "Down") {
        this.galleryOverlay = false;
      }
    },
    viewImageFullscreen(key) {
      this.selectedGalleryImage = key;
      this.galleryOverlay = true;
    },
    unitSelected(unit) {
      this.$emit("selected-unit", unit);
    },
    copyToClipboard() {
      let copyText = window.location.href;
      navigator.clipboard.writeText(copyText).then(() => {
        alert("Link to page copied to clipboard: \n" + window.location.href);
      });
    },
    onCarouselImageLoad(index) {
      const img = this.$refs.carouselImgs[index]?.$el?.querySelector("img");
      if (!img || !img.naturalWidth || !img.naturalHeight) return;

      const aspectRatio = img.naturalHeight / img.naturalWidth;
      const carouselWidth = img.clientWidth;
      const height = carouselWidth * aspectRatio;

      this.$set(this.carouselImageHeights, index, height);

      // Update the carousel container height
      this.$nextTick(() => {
        const carouselEl = img.closest(".v-carousel");
        if (carouselEl) {
          carouselEl.style.height = height + "px";
        }
      });
    },
    updateCarouselHeights() {
      this.$nextTick(() => {
        const carouselImages = this.$el.querySelectorAll(
          ".rentpress-floorplan-carousel-image img"
        );
        carouselImages.forEach((img, index) => {
          if (!img.naturalWidth || !img.naturalHeight) return;
          const aspectRatio = img.naturalHeight / img.naturalWidth;
          const width = img.clientWidth;
          const height = width * aspectRatio;
          this.$set(this.carouselImageHeights, index, height);
          const carouselEl = img.closest(".v-carousel");
          if (carouselEl) carouselEl.style.height = height + "px";
        });
      });
    }
  },
  watch: {
    galleryOverlay: function() {
      if (this.galleryOverlay) {
        document.documentElement.style.overflow = "hidden";
        return;
      }
      document.documentElement.style.overflow = "auto";
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateCarouselHeights);
  },
  mounted: function() {
    const urlParams = new URLSearchParams(window.location.search);
    const unitCodeFromUrl = urlParams.get("unit_code");
    const unitNameFromUrl = urlParams.get("unit_name");

    let preselectUnit = null;

    if (unitCodeFromUrl) {
      preselectUnit = this.floorplan.units.find(
        u => u.unit_code === unitCodeFromUrl
      );
    } else if (unitNameFromUrl) {
      const searchName = unitNameFromUrl.trim().toLowerCase();
      preselectUnit = this.floorplan.units.find(
        u => (u.unit_name ?? "").trim().toLowerCase() === searchName
      );
    }

    if (preselectUnit) {
      this.selectThisUnit(preselectUnit);
      this.$nextTick(() => {
        this.scrollUnitIntoView(preselectUnit);
      });
    }
    if (this.floorplan.units.length == 1) {
      const firstUnit = this.floorplan.units[0];
      this.selectThisUnit(firstUnit);
    }
    this.setUpCarousel();
    if (
      this.floorplan.floorplan_sqft_max &&
      this.floorplan.floorplan_sqft_min
    ) {
      this.sqFtText =
        this.floorplan.floorplan_sqft_max !== this.floorplan.floorplan_sqft_min
          ? `${this.floorplan.floorplan_sqft_min} - ${this.floorplan.floorplan_sqft_max} Sq. Ft`
          : `${this.floorplan.floorplan_sqft_max} Sq. Ft.`;
    } else if (this.floorplan.floorplan_sqft_max) {
      this.sqFtText = `${this.floorplan.floorplan_sqft_max} Sq. Ft.`;
    } else if (this.floorplan.floorplan_sqft_min) {
      this.sqFtText = `${this.floorplan.floorplan_sqft_min} Sq. Ft.`;
    }

    this.hasUnits =
      this.floorplan.units && this.floorplan.units.length > 0 ? true : false;

    this.smsLink = "sms:?body=" + encodeURIComponent(window.location.href);

    this.facebookLink =
      "https://www.facebook.com/sharer.php?u=" + window.location.href;

    this.emailLink =
      "mailto:?subject=Check Out This Apartment&body=I liked this apartment " +
      this.floorplan.floorplan_post_title +
      ". What do you think? " +
      window.location.href;

    window.addEventListener("resize", this.updateCarouselHeights);
  },
  components: { unitCard }
};
</script>

<style>
.circle-button {
  border: 3px solid var(--primary-color);
  height: 100px;
  border-radius: 50%;
  -moz-border-radius: 50%;
  -webkit-border-radius: 50%;
  width: 100px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  text-align: center;
  align-content: center;
  justify-content: center;
  margin-right: 5px;
  text-decoration: none;
}
.circle-button .v-icon {
  flex: 0 0 100%;
}
.circle-button p {
  width: 80%;
  font-size: 1.2em;
  line-height: 1.3;
  margin: 0;
}

.rentpress-floorplan-carousel-image {
  cursor: zoom-in;
  width: 100%;
  aspect-ratio: auto !important;
}

.carousel-column {
  display: flex;
  flex-direction: column;
  justify-content: center; /* vertical center content inside this column */
}

.rentpress-floorplan-carousel-item {
  margin: 0 auto;
  max-width: 100%;
}

.rentpress-floorplan-carousel-image-lightbox {
  background-color: transparent !important;
}

.rentpress-lightbox-image-wrapper,
.v-carousel__controls {
  filter: drop-shadow(0px 0px 10px #2c2c2cab);
  border-radius: 4px;
}

.rentpress-units-selection-container {
  display: flex;
  overflow-x: auto !important;
  flex-wrap: nowrap;
  align-items: center;
  flex-direction: row;
  width: 90%;
}

#rentpress-units-term-menu-list .term-list-item:hover {
  background-color: var(--primary-color) !important;
  color: white !important;
  font-weight: 900;
}

#rentpress-units-term-menu-list .term-list-item-best {
  color: var(--primary-color) !important;
}

.floorplan-form-pop-up {
  padding: 2%;
}

.floorplan-form-pop-up-close-btn {
  padding-top: 5%;
}
.rentpress-magnify-carousel-image-wrapper {
  display: block;
  position: relative;
  bottom: -30px;
}

.rentpress-units-special-container {
  padding: 1rem;
}

#rentpress-unit-features-list {
  columns: 2;
}
.rentpress-floorplan-information-title-price-wrapper {
  min-width: 70%;
  display: inline-block;
}
.rentpress-floorplan-iframe-buttons {
  display: inline-flex;
  align-items: center;
  width: 30%;
  min-width: 220px;
  flex-wrap: nowrap;
  align-content: center;
  justify-content: center;
}

.v-btn.rentpress-gallery-close-button:hover {
  background-color: v-bind("primaryColor") !important;
  filter: brightness(85%) !important;
  border-radius: 50% !important;
}

.rentpress-floorplan-information-top-wrapper {
  display: flex;
}
.rentpress-units-selection-wrapper {
  display: flex;
  align-items: center;
  flex-direction: row;
}
.rentpress-floorplan-under-image-buttons {
  display: none;
}

.rentpress-similar-floorplans {
  margin-bottom: 1rem;
}

.rentpress-unit-slider-item {
  display: inline-block;
  padding-right: 8px;
  padding-left: 0px;
  padding-top: 8px;
  padding-bottom: 16px;
  width: 260px;
}

.rentpress-units-sort-dropdown {
  opacity: 0.75;
  margin-top: 8px;
}

.rentpress-units-sort-dropdown:hover {
  opacity: 1;
}

.unit-sort-selection {
  padding-bottom: 15px;
}
.rentpress-units-sort-dropdown .v-input__append-inner {
  display: none !important;
}
.rentpress-units-sort-dropdown .v-input__slot,
.rentpress-units-sort-dropdown input {
  cursor: default !important;
}

.rentpress-units-selection-wrapper button {
  padding: unset;
  margin: unset;
}

@media only screen and (max-width: 800px) {
  .rentpress-floorplan-information-title-price-wrapper {
    min-width: 60%;
  }
}

@media only screen and (max-width: 580px) {
  .rentpress-floorplan-title-buttons {
    display: none;
  }
  .rentpress-floorplan-under-image-buttons {
    display: inline-flex;
    justify-content: center;
    width: 100%;
    margin-top: 20px;
  }
  .rentpress-single-floorplan-main-info-block {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  .rentpress-single-floorplan-main-info-block span {
    width: 100%;
  }
  .rentpress-floorplan-information-dot-sep {
    display: none;
  }
  .rentpress-share-icon-copy {
    background: unset !important;
  }
}
</style>
